<template>
  <div class="cohort-edit-wizard edyst-form-group">
    <div class="tab-container question-bank-cohort-form-1">
      <h3 class="text-center mb-4 mt-12">
        {{
          editMode
            ? `Edit Question Bank Cohort: ${this.cohortObj.name}`
            : "Create New Question Bank Cohort"
        }}
      </h3>
      <button
        :disabled="!editMode && tabIndex !== tabs.length - 1"
        class="p-2 update-button bg-green text-white rounded-sm shadow-md px-8"
        @click="addNewCohort"
      >
        {{ editMode ? "Update" : "Save" }}
      </button>
      <div class="stepper-container my-4">
        <button
          v-for="(tab, idx) in tabs"
          :key="idx + tab"
          class="px-1 action-btn rounded-sm truncate text-sm font-normal text-white mr-2"
          :disabled="!editMode && tabIndex < idx"
          :style="
            currentTab === tab
              ? '#6259c3'
              : editMode || tabIndex > idx
              ? 'background: #9f96ff;'
              : 'background: grey'
          "
          @click="moveToTab(idx)"
        >
          {{ idx + 1 }}. {{ tab }}
        </button>
      </div>

      <!-- ============================================= basic details ======================================================= -->
      <div v-if="currentTab === 'Basic Details'" class="fadeIn">
        <h4 class="text-center font-normal mb-4 border-b pb-2">
          {{ currentTab }}
        </h4>
        <div class="flex items-center">
          <label for="name"
            >Cohort Name <span class="text-red ml-2">*</span></label
          >
          <div class="w-full">
            <input
              id="cohort name"
              v-model="cohortObj.name"
              v-validate="'required'"
              name="cohort name"
              type="text"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("cohort name") }}
            </small>
          </div>
        </div>
        <div class="flex items-center">
          <label for="max size"
            >Maximum Size <span class="text-red ml-2">*</span></label
          >
          <div class="w-full">
            <input
              v-model="cohortObj.size_max"
              v-validate="'required'"
              type="number"
              step="1"
              name="maximum size"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("maximum size") }}
            </small>
          </div>
        </div>
        <div class="flex items-center mt-3">
          <label for="start date"
            >Start Date <span class="text-red ml-2">*</span></label
          >
          <div class="w-full">
            <EdystDatePicker
              v-model="cohortObj.start_date"
              :selected="
                cohortObj.start_date ? new Date(cohortObj.start_date) : null
              "
              placeholderText="Select start date"
              class="w-full"
              name="start date"
              :class="veeValidateErrors.first('start date') ? 'is-invalid' : ''"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("start date") }}
            </small>
            <input
              v-model="cohortObj.start_date"
              v-validate="'required'"
              type="hidden"
              name="start date"
            />
          </div>
        </div>
        <div class="flex items-center mt-3">
          <label for="end date"
            >End Date <span class="text-red ml-2">*</span></label
          >
          <div class="w-full">
            <EdystDatePicker
              v-model="cohortObj.end_date"
              :selected="
                cohortObj.end_date ? new Date(cohortObj.end_date) : null
              "
              placeholderText="Select end date"
              class="w-full"
              :class="veeValidateErrors.first('end date') ? 'is-invalid' : ''"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("end date") }}
            </small>
            <input
              v-model="cohortObj.end_date"
              v-validate="'required'"
              type="hidden"
              name="end date"
            />
          </div>
        </div>
        <div class="flex items-start">
          <label for="end date" class="mt-4"
            >Thumbnail <span class="text-red ml-2">*</span></label
          >
          <div class="w-full mt-3">
            <ThumbnailGenerator
              v-model="cohortObj.thumbnail_url"
              :text="cohortObj.name"
              context_type="qb_cohort"
              name="thumbnail"
              style="width:15rem"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("thumbnail") }}
            </small>
            <input
              v-model="cohortObj.thumbnail_url"
              v-validate="'required'"
              type="hidden"
              name="thumbnail"
            />
          </div>
        </div>
      </div>

      <!-- ============================================= Tags ======================================================= -->
      <div v-if="currentTab === 'Tags'" class="fadeIn">
        <h4 class="text-center font-normal mb-4 border-b pb-2">
          {{ currentTab }}
        </h4>
        <div class="flex items-center">
          <label for="name">Select Tags</label>
          <div class="bg-white rounded px-8 w-full">
            <MultiSelect
              v-model="cohortObj.cohort_tags"
              :options="allCohortTags"
              track-by="id"
              label="name"
              tag-placeholder="Add as new tag"
              placeholder="Select or add a tag"
              :multiple="true"
              :taggable="true"
              @tag="addNewCohortTag"
            />
          </div>
        </div>
      </div>

      <!-- ==================== Group Creation for Question Bank COhort ========================= -->

      <div v-if="currentTab === 'Group Creation'" class="fadeIn mentors-tab">
        <h4 class="text-center font-normal mb-4 border-b pb-2">
          {{ currentTab }}
        </h4>
        <div class="text-right mb-8">
          <button
            class="rounded px-8 bg-grey hover:text-white action-btn text-white shadow-md"
            @click="getCohortGroup"
          >
            + Add Group
          </button>
        </div>
        <div class="shadow-md">
          <table>
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Group Tag</th>

                <th>Group Companies</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(group, i) in groups" :key="i">
                <td>{{ group.name }}</td>
                <td>{{ group.tag }}</td>
                <td>{{ group.companies.map(item => item.name) }}</td>
                <td>{{ group.base_price }}</td>
                <td>
                  <i
                    class="material-icons group-edit-icon cursor-pointer"
                    @click="getCohortGroup(i)"
                    >edit</i
                  >
                </td>
                <td>
                  <i
                    class="material-icons group-delete-icon cursor-pointer"
                    @click="deleteCohortGroup(i)"
                    >delete</i
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ======================================= Select MENTORS For a Question Bank COhort ====================================== -->

      <div v-if="currentTab === 'Mentors'" class="fadeIn mentors-tab">
        <h4 class="text-center font-normal mb-4 border-b pb-2">
          {{ currentTab }}
        </h4>
        <div class="mentors">
          <p v-if="mentors.length === 0" class="text-center mt-8">
            No mentor added!
          </p>
          <div
            v-for="(mentor, index) in mentors"
            :key="index"
            class="w-full relative"
          >
            <div class="mentor-container shadow-md">
              <div class="flex items-center w-full mb-2">
                <label :for="`add-mentor-${index}`" class="w-1/4"
                  >Mentor Email:</label
                >
                <div class="w-3/4 mr-4">
                  <input
                    :id="`add-mentor-${index}`"
                    v-model="mentor.email"
                    type="text"
                    class="w-full"
                  />
                </div>
              </div>
              <div class="flex items-center w-full">
                <label class="w-1/4" @click="focusDropdown(index)"
                  >Add Mentor Tags:</label
                >
                <div class="bg-white rounded w-3/4  mr-4 mb-3">
                  <MultiSelect
                    ref="tagsSelecter"
                    v-model="mentor.tags"
                    :options="allTags"
                    tag-placeholder="Add this as new tag"
                    placeholder="Search or add a tag"
                    :multiple="true"
                    :taggable="true"
                    @tag="addNewMentorTag($event, index)"
                  />
                </div>
              </div>
              <div class="remove-mentor">
                <button
                  class="rounded p-1 hover:bg-grey-light hover:text-white text-xs"
                  @click="removeMentor(index)"
                >
                  &#x274C;
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="text-right">
          <button
            class="rounded px-3 py-2 bg-grey hover:text-white action-btn text-white shadow-md"
            @click="addNewMentor"
          >
            + Add Mentor
          </button>
        </div>
      </div>

      <!-- ############################ Welcomers for Question Bank Cohort #################################### -->

      <div
        v-if="
          currentTab === 'Welcomer' &&
            cohortObj.data &&
            cohortObj.data.welcomer_details
        "
        class="fadeIn"
      >
        <h4 class="text-center font-normal mb-4 border-b pb-2">
          {{ currentTab }}
        </h4>
        <p class="text-sm text-grey-dark mb-4">
          * To enable welcomer message please add welcomer email, online message
          and offine message. or
          <button
            class="px-2 bg-grey-light rounded-sm font-normal"
            @click="clearWelcomerDetails"
          >
            clear
          </button>
          all fields to disable it.
        </p>
        <div class="flex items-baseline">
          <label for="welcomer email" class=""
            >Enter Welcomer Email<small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("welcomer email") }}
            </small></label
          >
          <input
            id="welcomer email"
            v-model.trim="cohortObj.data.welcomer_details.email"
            v-validate="welcomerDetailsAdded ? 'required' : ''"
            placeholder="Enter welcomer email"
            name="welcomer email"
            type="email"
          />
        </div>

        <div class="flex items-baseline">
          <label for="online message" class=""
            >Online Message
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("online message") }}
            </small></label
          >
          <textarea
            id="online message"
            v-model.trim="cohortObj.data.welcomer_details.online_message"
            v-validate="welcomerDetailsAdded ? 'required' : ''"
            placeholder="Enter online message"
            class="w-2/3"
            name="online message"
            style="height: 10rem"
          ></textarea>
        </div>

        <div class="flex items-baseline">
          <label for="offline message" class=""
            >Offline Message
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("offline message") }}
            </small></label
          >
          <textarea
            id="offline message"
            v-model.trim="cohortObj.data.welcomer_details.offline_message"
            v-validate="welcomerDetailsAdded ? 'required' : ''"
            placeholder="Enter offline message"
            name="offline message"
            style="height: 10rem"
          ></textarea>
        </div>
      </div>

      <!-- ################################# Settings for Question Bank Cohort ############################################ -->

      <div
        v-if="currentTab === 'Settings' && cohortObj.data"
        class="fadeIn settings"
      >
        <h4 class="text-center font-normal mb-4 border-b pb-2">
          {{ currentTab }}
        </h4>
        <div class="flex">
          <label>Pop-Up Form</label>
          <MultiSelect
            ref="tagsSelecter"
            v-model="cohortFormKey"
            :options="formKeysOptions"
            placeholder="Select Pop-Up form"
          />
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            User Chat
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="user chat enable"
              v-model="cohortObj.data.disableUserChat"
              :value="false"
              type="radio"
              name="user chat"
            />
            <label for="user chat enable">Enable</label>
            <input
              id="user chat disable"
              v-model="cohortObj.data.disableUserChat"
              :value="true"
              type="radio"
              name="user chat"
            />
            <label for="user chat disable">Disable</label>
          </div>
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            Group Chat
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="group chat enable"
              v-model="cohortObj.data.disableGroupChat"
              :value="false"
              type="radio"
              name="group chat"
            />
            <label for="group chat enable">Enable</label>
            <input
              id="group chat disable"
              v-model="cohortObj.data.disableGroupChat"
              :value="true"
              type="radio"
              name="group chat"
            />
            <label for="group chat disable">Disable</label>
          </div>
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            Notifications Events & Toasts
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="notification enable"
              v-model="cohortObj.data.disableNotificationEvents"
              :value="false"
              type="radio"
              name="notification"
            />
            <label for="notification enable">Enable</label>
            <input
              id="notification disable"
              v-model="cohortObj.data.disableNotificationEvents"
              :value="true"
              type="radio"
              name="notification"
            />
            <label for="notification disable">Disable</label>
          </div>
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            Email Notifications
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="email enable"
              v-model="cohortObj.data.disableNotificationEmails"
              :value="false"
              type="radio"
              name="email notification"
            />
            <label for="email enable">Enable</label>
            <input
              id="email disable"
              v-model="cohortObj.data.disableNotificationEmails"
              :value="true"
              type="radio"
              name="email notification"
            />
            <label for="email disable">Disable</label>
          </div>
        </div>
        <div v-if="editMode" class="flex items-center justify-start">
          <label class="">Peer Chat Cohorts</label>
          <div class="w-full">
            <CohortMultiselect
              v-model="peerCohortChatMap"
              :skipCohortID="cohortObj.id"
              @input="filterPeerCohortMap"
            />
          </div>
        </div>
      </div>

      <!-- ################################### Question Bank Cohort Social Share ############################################### -->

      <div
        v-if="currentTab === 'Social Share' && cohortObj.data"
        class="fadeIn"
      >
        <h4 class="text-center font-normal mb-4 border-b pb-2">
          {{ currentTab }}
        </h4>
        <div class="flex items-center mb-4 rounded-sm hover:bg-grey-light px-2">
          <input
            id="enable social share"
            v-model="cohortObj.social_sharing_enabled"
            class="mr-4"
            type="checkbox"
          />
          <label
            for="enable social share"
            style="width: 100%;padding: 0.5rem;"
            class="cursor-pointer select-none"
            >Enable social sharing popup for the cohort</label
          >
        </div>
        <div
          class="fadeIn"
          :class="{
            'social-sharing-disabled pointer-events-none select-none': !cohortObj.social_sharing_enabled,
          }"
        >
          <div class="flex items-start">
            <div class="w-1/3">
              <label for="share message" style="width: 100%;" class="mt-3"
                >Share Message <span class="text-red">*</span></label
              >
              <div class="text-xs text-grey-dark">
                Only plain text is accepted<br />
                New lines can be added
              </div>
            </div>
            <div class="w-full">
              <textarea
                id="share message"
                v-model="cohortObj.social_sharing_message"
                placeholder="Enter share message"
                class="w-2/3"
                style="width: 100%;height: 10rem;"
              ></textarea>
              <span
                class="text-xs text-grey-dark text-right block"
                :class="{ 'text-red-light': shareMessageCharCount > 140 }"
                >Characters count: <span>{{ shareMessageCharCount }}</span
                >/140
              </span>
            </div>
          </div>
          <div class="flex items-start">
            <div class="w-1/3">
              <label for="signup-url" class="mt-3"
                >Signup URL <span class="text-red">*</span></label
              >
            </div>
            <div class="w-full">
              <textarea
                id="signup-url"
                v-model="cohortObj.social_sharing_signup_url"
                v-validate="'url'"
                placeholder="Enter signup URL"
                style="width:100%;height: 4rem;"
                name="Signup URL"
              ></textarea>
              <small class="err-msg text-red block mt-2">
                {{ veeValidateErrors.first("Signup URL") }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- navigation buttons -->
      <div class="text-right mt-4 navigation-buttons-container">
        <button
          class="action-btn font-normal text-white px-8 rounded-sm mr-2 shadow-md"
          :disabled="!hasPrevTab"
          @click="validateAndNavigate('prev')"
        >
          ‹‹ Prev
        </button>
        <button
          class="action-btn font-normal text-white px-8 rounded-sm ml-2 shadow-md"
          :disabled="!hasNextTab"
          @click="validateAndNavigate('next')"
        >
          Next ››
        </button>
      </div>

      <VModal
        name="add-group-modal"
        :click-to-close="false"
        width="861.05px"
        height="65%"
        style="overflow: visible !important;"
      >
        <div class="group-container w-3/4 m-auto">
          <br /><br />
          <div style="position: absolute; top: 25px; right: 25px;">
            <button @click="clearAndCloseCohortGroup">
              &#10006;
            </button>
          </div>
          <div v-if="group_index >= 0 && groups[group_index]">
            <h2 class="text-center mb-10 border-b pb-2">
              Update a Group - {{ cohortObj.group_name }}
            </h2>
          </div>
          <div v-else>
            <h2 class="text-center mb-10 border-b pb-2">Create a Group</h2>
          </div>
          <div class="flex items-center items-baseline mb-4">
            <label for="group name">Group Name: </label>
            <div class="w-full m-2 input-wrapper">
              <input
                v-model="cohortObj.group_name"
                v-validate="'required'"
                name="Group name"
                type="text"
                placeholder="Add Group Name"
                label="name"
              />
              <small class="err-msg text-red block mt-2">
                {{ veeValidateErrors.first("Group name") }}
              </small>
            </div>
          </div>
          <div class="flex items-center items-baseline mb-4">
            <label for="group tag">Group Tag: </label>
            <div class="w-full m-2 input-wrapper">
              <input
                v-model="cohortObj.group_tag"
                v-validate="'required'"
                name="Group tag"
                type="text"
                placeholder="Add Group Tag"
              />
              <small class="err-msg text-red block mt-2">
                {{ veeValidateErrors.first("Group tag") }}
              </small>
            </div>
          </div>

          <div class="flex items-center items-baseline mb-4">
            <label class="group companies">Group Company</label>
            <div class="w-full m-2 input-wrapper select-group-companies">
              <VueSelect
                v-model="selectedCompany"
                :options="companyOptions"
                name="Group companies"
                label="name"
                multiple
                :closeOnSelect="false"
              />
            </div>
          </div>

          <div class="flex items-center items-baseline mb-4">
            <label for="group price">Price (in paise, 0 if free): </label>
            <div class="w-full m-2 input-wrapper">
              <input
                v-model="cohortObj.group_price"
                v-validate="'required'"
                name="Group price"
                placeholder="Add Group Tag"
                type="number"
                step="100"
              />
              <small class="err-msg text-red block mt-2">
                {{ veeValidateErrors.first("Group price") }}
              </small>
            </div>
          </div>

          <div class="text-right p-4 mt-2 mr-10">
            <button
              class="text-right bg-green hover:bg-green-dark px-10 py-2 text-white"
              @click="createOrUpdateCohortGroup"
            >
              save Group
            </button>
          </div>
        </div>
      </VModal>
    </div>
  </div>
</template>

<script>
import VueSelect from "vue-select"
import MultiSelect from "vue-multiselect"
import siteConfig from "@/utils/siteConfig"
import { getCohortTags } from "@/apis/cohorts/cohorts"
import CohortMultiselect from "@/components/shared/CohortMultiselect"
import EdystDatePicker from "@/components/ReactDatePicker/DatePicker"
import { hasKey } from "@/utils/objectUtil"
import ThumbnailGenerator from "@/components/shared/ThumbnailGenerator"

export default {
  components: {
    VueSelect,
    CohortMultiselect,
    MultiSelect,
    EdystDatePicker,
    ThumbnailGenerator,
  },
  props: {
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      activeTab: "basicDetails",
      tabs: [
        "Basic Details",
        "Tags",
        "Group Creation",
        "Mentors",
        "Welcomer",
        "Settings",
        "Social Share",
      ],
      tabIndex: 0,
      cohortObj: {
        name: "",
        size_max: null,
        start_date: "",
        end_date: "",
        group_name: "",
        group_tag: "",
        cohort_tags: [],
        group_price: null,
        data: {
          mentors: [
            {
              email: undefined,
              tags: [],
            },
          ],
          disableUserChat: false,
          disableGroupChat: true,
          disableNotificationEmails: true,
          disableNotificationEvents: false,
          welcomer_details: {
            email: "",
            offline_message: "",
            online_message: "",
          },
        },
        social_sharing_enabled: false,
        social_sharing_message:
          "Hi! I'm inviting you to Edyst. Find exciting courses and internships with certificates.",
        social_sharing_signup_url: "https://edyst.com",
        thumbnail_url: "",
      },
      formKeysOptions: [],
      selectedCompany: [],
      companyOptions: [],
      cohortFormKey: undefined,
      peerCohortChatMap: undefined,
      prevPeerCohortChatMap: undefined,
      groups: [],
      unlink_groups: [],
      mentors: [],
      allTags: [],
      allCohortTags: [],
      group_index: null,
    }
  },
  computed: {
    welcomerDetailsAdded() {
      /* checks if any of the input fields has any data
      if true then mark others as required in v-validate attr */
      return (
        this.cohortObj.data.welcomer_details &&
        (this.cohortObj.data.welcomer_details.email ||
          this.cohortObj.data.welcomer_details.online_message ||
          this.cohortObj.data.welcomer_details.offline_message)
      )
    },
    hasNextTab() {
      return this.tabIndex < this.tabs.length - 1
    },
    hasPrevTab() {
      return this.tabIndex > 0
    },
    currentTab() {
      return this.tabs[this.tabIndex]
    },
    shareMessageCharCount() {
      if (
        this.cohortObj.social_sharing_enabled &&
        this.cohortObj.social_sharing_message
      ) {
        return this.cohortObj.social_sharing_message.length
      }
      return 0
    },
  },
  created() {
    if (this.editMode) {
      this.getCohortData()
    } else {
      this.fetchCohortTags()
    }
    this.getCompaniesList()
    this.getFormKeys()
  },
  methods: {
    getFormKeys() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}content`,
      }).then(res => {
        this.formKeysOptions = res.data.filter(item =>
          item.startsWith("form-schema-")
        )
      })
    },
    clearWelcomerDetails() {
      this.cohortObj.data.welcomer_details = {
        email: "",
        online_message: "",
        offline_message: "",
      }
    },
    filterPeerCohortMap() {
      if (this.peerCohortChatMap) {
        const newPeerCohortChatMap = {}
        /* modify the dict to the format expected in backend */
        Object.keys(this.peerCohortChatMap).forEach(cid => {
          newPeerCohortChatMap[cid] = {
            cohort_name: this.peerCohortChatMap[cid].name,
          }
        })
        this.peerCohortChatMap = newPeerCohortChatMap
      }
    },
    moveToTab(tabIndex) {
      if (!this.editMode && tabIndex > this.tabIndex) {
        return
      }
      this.tabIndex = tabIndex
    },
    addNewMentor() {
      this.mentors.push({
        email: undefined,
        tags: [],
      })
    },
    focusDropdown(index) {
      this.$nextTick(() => {
        /* focus newly added select box */
        this.$refs.tagsSelecter[index].$el.focus()
      })
    },
    removeMentor(index) {
      this.mentors.splice(index, 1)
    },
    addNewMentorTag(tag, index) {
      this.mentors[index].tags.push(tag)
      if (!this.allTags.includes(tag)) {
        this.allTags.push(tag)
      }
      this.focusDropdown(index)
    },
    addPeerCohortId(peerCohortId) {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const peerCohortData = res.data.data || {}
        if (!peerCohortData.peerCohortChatMap) {
          peerCohortData.peerCohortChatMap = {}
        }
        const { peerCohortChatMap } = peerCohortData
        if (peerCohortChatMap[this.cohortObj.id]) {
          return
        }
        peerCohortChatMap[this.cohortObj.id] = {
          cohort_name: this.cohortObj.name,
          disableGroupChat: this.disableGroupChat,
          disableUserChat: this.disableUserChat,
        }

        this.axios({
          method: "patch",
          url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-access-token": localStorage.token,
          },
          data: { data: peerCohortData },
        })
          .then(() => {
            console.log("added peer cohort ID:", peerCohortId)
          })
          .catch(err => {
            alert("Failed!", err)
            throw new Error(err)
          })
      })
    },
    deletePeerCohortId(peerCohortId) {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const peerCohortData = res.data.data || {}
        if (!peerCohortData.peerCohortChatMap) {
          peerCohortData.peerCohortChatMap = {}
        }
        const { peerCohortChatMap } = peerCohortData
        if (peerCohortChatMap[this.cohortObj.id]) {
          delete peerCohortChatMap[this.cohortObj.id]
        } else {
          return
        }

        this.axios({
          method: "patch",
          url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-access-token": localStorage.token,
          },
          data: { data: peerCohortData },
        })
          .then(() => {
            console.log("deleted peer cohort ID:", peerCohortId)
          })
          .catch(err => {
            alert("Failed!")
            throw new Error(err)
          })
      })
    },
    validateAndNavigate(way = "next") {
      if (
        this.currentTab === "Welcomer" &&
        this.cohortObj.data.welcomer_details &&
        this.cohortObj.data.welcomer_details.email &&
        (!this.cohortObj.data.welcomer_details.online_message ||
          !this.cohortObj.data.welcomer_details.offline_message)
      ) {
        this.$noty.error(
          "You added email for welcomer, please also add both online and offline messages else remove welcomer email"
        )
        return
      }
      this.$validator.validateAll().then(result => {
        if (!result) {
          this.$noty.error("Please fill all the required fields marked with *")
          return
        }
        if (way === "next" && this.hasNextTab) {
          this.tabIndex += 1
        } else if (way === "prev" && this.hasPrevTab) {
          this.tabIndex -= 1
        }
      })
    },
    async addNewCohort() {
      if (!this.editMode && this.tabIndex !== this.tabs.length - 1) {
        this.$noty.error("Please Fill all the details before saving.")
        return
      }

      if (this.prevPeerCohortChatMap) {
        Object.keys(this.prevPeerCohortChatMap).forEach(prevPeerCohortId => {
          if (
            !this.peerCohortChatMap ||
            !this.peerCohortChatMap[prevPeerCohortId]
          ) {
            this.deletePeerCohortId(prevPeerCohortId)
          }
        })
      }

      if (this.peerCohortChatMap) {
        Object.keys(this.peerCohortChatMap).forEach(peerCohortId => {
          if (
            !this.prevPeerCohortChatMap ||
            !this.prevPeerCohortChatMap[peerCohortId]
          ) {
            this.addPeerCohortId(peerCohortId)
          }
        })
      }

      if (this.cohortObj.social_sharing_enabled) {
        if (
          !this.cohortObj.social_sharing_message ||
          !this.cohortObj.social_sharing_signup_url
        ) {
          this.$noty.error(
            "Please add share message and signup url for social sharing"
          )
          return
        }
      }

      const result = await this.$validator.validateAll()
      if (!result) {
        this.$noty.error("Please fix the errors and try again")
        return
      }

      const obj = {
        ...JSON.parse(JSON.stringify(this.cohortObj)),
        cohort_tags: this.cohortObj.cohort_tags.map(t => t.name),
      }

      /* post above validation if we find that welcomer email is not
       present then we delete welcomer_details key from data */
      if (obj.data.welcomer_details && !obj.data.welcomer_details.email) {
        Reflect.deleteProperty(obj.data, "welcomer_details")
      }

      obj.data.user_forms = {
        ...obj.user_forms,
        home_popup: this.cohortFormKey,
      }

      obj.data.peerCohortChatMap = this.peerCohortChatMap
      obj.groups = this.groups
      obj.unlink_groups = this.unlink_groups

      if (this.mentors) {
        // Removing mentors with empty email address
        let mentors = this.mentors.filter(item => item.email)
        mentors = mentors.map(mentor => {
          mentor.tags = mentor.tags.map(tag => tag.trim())
          return mentor
        })
        obj.data.mentors = mentors
      } else {
        obj.data.mentors = []
      }

      const url = this.editMode
        ? `${siteConfig.siteURL}question_bank/cohorts/${this.$route.params.id}`
        : `${siteConfig.siteURL}question_bank/cohorts`
      this.axios({
        method: this.editMode ? "PUT" : "POST",
        url,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
        data: obj,
      }).then(res => {
        if (!res.data.status) {
          this.$swal({
            icon: "error",
            text: res.data.msg,
          })
        } else {
          this.prevPeerCohortChatMap = this.peerCohortChatMap
          this.$noty.info(`Cohort ${this.editMode ? "Updated" : "Saved"}! ✔`)
          if (!this.editMode) {
            this.$router.push("/question-bank/cohort/")
          }
        }
      })
    },
    modifyCohortGroup(index) {
      const group = this.groups[index]
      this.cohortObj.group_name = group.name
      this.cohortObj.group_tag = group.tag
      this.selectedCompany = group.companies
      this.cohortObj.group_price = group.base_price
    },
    getCohortGroup(index = null) {
      if (index >= 0) {
        this.group_index = index
        this.modifyCohortGroup(index)
      }
      this.$modal.show("add-group-modal")
    },
    deleteCohortGroup(index) {
      const obj = this.groups[index]
      this.unlink_groups.push(obj)
      this.groups.splice(index, 1)
    },
    clearAndCloseCohortGroup() {
      this.cohortObj.group_name = ""
      this.cohortObj.group_tag = ""
      this.selectedCompany = []
      this.cohortObj.group_price = null

      this.$modal.hide("add-group-modal")
      this.group_index = null
    },
    async createOrUpdateCohortGroup() {
      const result = await this.$validator.validateAll()
      if (!result) {
        this.$noty.error("Please fix the errors and try again")
        return
      }

      const cohortObj = await this.cohortObj

      if (this.group_index !== null && this.group_index >= 0) {
        const obj = this.groups[this.group_index]
        obj.name = cohortObj.group_name
        obj.tag = cohortObj.group_tag
        obj.companies = this.selectedCompany.map(item => ({
          id: item.id,
          name: item.name,
        }))
        obj.base_price = cohortObj.group_price
      } else {
        this.groups.push({
          name: cohortObj.group_name,
          tag: cohortObj.group_tag,
          companies: this.selectedCompany.map(item => ({
            id: item.id,
            name: item.name,
          })),
          base_price: cohortObj.group_price,
        })
      }
      this.clearAndCloseCohortGroup()
    },
    getCohortData() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}question_bank/cohorts/${this.$route.params.id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const cohortObj = res.data.cohort
        cohortObj.start_date = cohortObj.start_date
        cohortObj.end_date = cohortObj.end_date
        if (!cohortObj.thumbnail_url) cohortObj.thumbnail_url = ""
        cohortObj.size_max = 1
        // simplify tags data shape
        cohortObj.cohort_tags = cohortObj.cohort_tags[0].cohort_tag_id

        this.cohortFormKey =
          (cohortObj.data &&
            cohortObj.data.user_forms &&
            cohortObj.data.user_forms.home_popup) ||
          undefined

        if (!hasKey(cohortObj.data, "disableGroupChat")) {
          cohortObj.data.disableGroupChat = true
        }
        if (!hasKey(cohortObj.data, "disableUserChat")) {
          cohortObj.data.disableUserChat = false
        }
        if (!hasKey(cohortObj.data, "disableNotificationEvents")) {
          cohortObj.data.disableNotificationEvents = false
        }
        if (!hasKey(cohortObj.data, "disableNotificationEmails")) {
          cohortObj.data.disableNotificationEmails = false
        }

        if (
          cohortObj.data &&
          cohortObj.data.mentors &&
          cohortObj.data.mentors.length > 0
        ) {
          this.mentors = cohortObj.data.mentors
        } else {
          this.addNewMentor()
        }

        if (cohortObj.data && cohortObj.data.mentors) {
          const { mentors } = cohortObj.data
          let selectedTags = []
          for (let i = 0; i < mentors.length; i++) {
            selectedTags.push(...mentors[i].tags)
          }
          selectedTags = new Set(selectedTags)
          this.allTags = [...selectedTags]
        }

        if (!hasKey(cohortObj.data, "welcomer_details")) {
          cohortObj.data.welcomer_details = {
            email: "",
            offline_message: "",
            online_message: "",
          }
        }

        this.peerCohortChatMap =
          (cohortObj.data && cohortObj.data.peerCohortChatMap) || undefined
        this.prevPeerCohortChatMap = this.peerCohortChatMap

        this.cohortObj = cohortObj

        if (cohortObj.groups && cohortObj.groups.length > 0) {
          cohortObj.groups.forEach(group => {
            this.groups.push({
              id: group.id,
              name: group.name,
              slug: group.slug,
              tag: group.tag,
              companies: group.group_companies.map(item => ({
                id: item.company.id,
                name: item.company.name,
              })),
              base_price: group.base_price,
            })
          })
        }
        this.fetchCohortTags(true)
      })
    },
    getCompaniesList() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}admin/company?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const companies = res.data.items
        this.companyOptions = companies.map(item => ({
          id: item.id,
          name: item.name,
        }))
      })
    },
    addNewCohortTag(item) {
      this.cohortObj.cohort_tags.push({ id: item, name: item })
    },
    fetchCohortTags(mapsTags = false) {
      getCohortTags()
        .then(res => {
          this.allCohortTags = res.data.items
          if (mapsTags) {
            /**
             * tag details are mapped from tag list api as
             * cohort details api doesn't return tag name
             */
            this.cohortObj.cohort_tags = this.allCohortTags.filter(item =>
              this.cohortObj.cohort_tags.find(id => id === item.id)
            )
          }
        })
        .catch(err => {
          console.error(err)
          this.$noty.error(
            "Failed to fetch tags, you may not be able to select existing tags"
          )
        })
    },
  },
}
</script>

<style scoped>
.cohort-edit-wizard {
  color: #424242;
}

.tab-container {
  position: relative;
  width: 100%;
  max-width: 60rem;
  height: 90vh;
  margin: auto;
  padding: 2rem;
  box-shadow: 0 0 25px silver;
}

.group-container {
  position: relative;
}

.navigation-buttons-container {
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  background: white;
}

.mentors {
  overflow: auto;
  height: 75%;
}

.question-bank-cohort-form-1 {
  border-width: 2px;
}

.question-bank-cohort-form-1 input {
  border-width: 1px;
  border-color: #030303e7;
  color: #424242;
}

.question-bank-cohort-form-1 label {
  width: 20%;
  padding: 0;
  color: #424242;
  text-align: left;
}

.select-group-companies .v-select .selected-tag {
  border-width: 2px;
  border-color: #030303e7;
}

.cohort-type-button {
  display: flex;
  width: 100%;
  max-width: 35rem;
  margin: auto;
  margin-top: 2rem;
  padding: 2rem 1rem;
  border: 1px solid #e7e7e7;
  border-radius: 3px;
  font-weight: normal;
  justify-content: space-between;
}

.cohort-type-button span:first-child {
  width: 30%;
  font-weight: bold;
  text-align: center;
}

.cohort-type-button span:last-child {
  width: 70%;
  text-align: left;
}

.cohort-type-button.selected {
  border: 1px solid #00d925;
  color: green;
  background: #f9fff9;
}

.cohort-type-button:focus:not(.selected),
.cohort-type-button:hover:not(.selected),
.cohort-type-button:active:not(.selected) {
  color: #525252;
  background: #f0f0f0;
}

.mentor-container {
  height: auto;
  margin-top: 1rem;
  padding: 0.5rem 2rem;
  border: 1px solid #dbdbdb;
  border-radius: 3px;
}

.question-bank-cohort-form-1 input[type="radio"] {
  width: 1.2rem;
  height: 1.2rem;
  margin-right: 0.5rem;
}

.cutoff-container {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid #cac7c7;
  background-color: #efefef;
}

.group-edit-icon {
  color: #5409aa;
}

.group-delete-icon {
  color: rgb(184, 30, 30);
}

.action-btn {
  width: auto;
  height: 2rem;
  background: #6259c3;
}

.cohort-type-err-message {
  max-width: 35rem;
  margin: auto;
  margin-top: 1rem;
}

.update-button {
  position: absolute;
  top: 2rem;
  right: 2rem;
  margin: 0;
}

.settings input[type="radio"] ~ label {
  width: 10%;
}

.course-create-input {
  width: 55%;
  margin: auto;
  margin-bottom: 8px;
}

.loading-icon {
  width: 2rem;
  height: 2rem;
}

.loading-icon img {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  animation: spin 1.5s infinite linear;
}

.social-sharing-disabled {
  opacity: 0.2;
}

button:disabled {
  background: grey;
}

.remove-mentor {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}

.mentors-tab {
  height: 80%;
}

@media screen and (max-height: 700px) {
  .mentors-tab {
    height: 70%;
  }
}

@media screen and (min-height: 900px) {
  .mentors-tab {
    height: 90%;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
